import { motion } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Wheat, Droplets, Flame, Sun, ShieldCheck } from "lucide-react";

const basmatiRice = [
  { name: "1121 White Sella", type: "Sella", family: "1121", desc: "Extra-long grain, parboiled white. Ideal for biryanis and pilafs." },
  { name: "1121 Golden Sella", type: "Sella", family: "1121", desc: "Golden hue, parboiled with excellent elongation after cooking." },
  { name: "1121 Steam", type: "Steam", family: "1121", desc: "Steamed basmati with natural white color and premium aroma." },
  { name: "1718 White", type: "Raw", family: "1718", desc: "Long grain raw basmati with delicate aroma and fluffy texture." },
  { name: "1718 Golden Sella", type: "Sella", family: "1718", desc: "Golden parboiled rice with consistent grain quality." },
  { name: "1718 Steam", type: "Steam", family: "1718", desc: "Steamed variety with excellent cooking properties." },
  { name: "1401 Raw Rice", type: "Raw", family: "1401", desc: "Traditional raw basmati, naturally aged for enhanced aroma." },
  { name: "1401 Golden Sella", type: "Sella", family: "1401", desc: "Golden parboiled with uniform grain size and color." },
  { name: "1401 Sella", type: "Sella", family: "1401", desc: "Classic sella with excellent shelf life and easy cooking." },
  { name: "1509 White Sella", type: "Sella", family: "1509", desc: "Budget-friendly sella with good grain length." },
  { name: "1509 Golden Sella", type: "Sella", family: "1509", desc: "Affordable golden sella, popular in African markets." },
  { name: "1509 Steam", type: "Steam", family: "1509", desc: "Steamed basmati with natural aroma at competitive pricing." },
  { name: "Pusa Steam Rice", type: "Steam", family: "Pusa", desc: "Premium Pusa variety with exceptional fragrance." },
  { name: "Pusa White Sella", type: "Sella", family: "Pusa", desc: "Parboiled Pusa basmati with superior grain quality." },
  { name: "Pusa Raw", type: "Raw", family: "Pusa", desc: "Raw Pusa basmati, known for its natural aroma and taste." },
];

const pesticideResidueRice = [
  { name: "1121 PRF Steam Rice", family: "1121", type: "Steam", desc: "Premium 1121 steam rice, certified pesticide residue free for health-conscious markets." },
  { name: "1121 PRF White Sella", family: "1121", type: "Sella", desc: "Parboiled white sella, pesticide residue free with excellent grain elongation." },
  { name: "1121 PRF Golden Sella", family: "1121", type: "Sella", desc: "Golden sella with pesticide residue free certification, ideal for premium exports." },
  { name: "1718 PRF White Sella", family: "1718", type: "Sella", desc: "Pesticide residue free white sella with consistent quality and aroma." },
  { name: "1718 PRF Steam", family: "1718", type: "Steam", desc: "Steamed 1718 basmati, certified free from pesticide residues." },
  { name: "1718 PRF Golden Sella", family: "1718", type: "Sella", desc: "Golden parboiled 1718, meeting strict pesticide residue free standards." },
  { name: "1509 PRF Steam Rice", family: "1509", type: "Steam", desc: "Affordable steamed basmati, pesticide residue free for safe consumption." },
  { name: "1509 PRF White Sella", family: "1509", type: "Sella", desc: "White sella 1509, certified pesticide residue free at competitive pricing." },
  { name: "1509 PRF Golden Sella", family: "1509", type: "Sella", desc: "Golden sella 1509, pesticide residue free with uniform grain color." },
  { name: "Pusa PRF Steam", family: "Pusa", type: "Steam", desc: "Pusa steamed basmati, pesticide residue free with exceptional fragrance." },
  { name: "Pusa PRF White Sella", family: "Pusa", type: "Sella", desc: "Pusa white sella, certified pesticide residue free for premium markets." },
  { name: "Sugandha PRF Golden Sella", family: "Sugandha", type: "Sella", desc: "Sugandha golden sella, pesticide residue free with natural sweet aroma." },
  { name: "Sugandha PRF White Sella", family: "Sugandha", type: "Sella", desc: "Sugandha white sella, certified pesticide residue free and aromatic." },
  { name: "Sharbati PRF White Sella", family: "Sharbati", type: "Sella", desc: "Sharbati white sella, pesticide residue free with soft texture after cooking." },
  { name: "Sharbati PRF Steam Rice", family: "Sharbati", type: "Steam", desc: "Sharbati steamed rice, certified pesticide residue free for export quality." },
];

const nonBasmatiRice = [
  { name: "IR64 Parboiled", type: "Parboiled", desc: "Medium grain, widely consumed across Africa and Asia." },
  { name: "IR64 Raw", type: "Raw", desc: "Raw IR64 with clean milling and uniform grain." },
  { name: "PR11 Sella", type: "Sella", desc: "Long grain non-basmati, popular in export markets." },
  { name: "PR11 Steam", type: "Steam", desc: "Steamed PR11 with consistent quality." },
  { name: "Sona Masoori", type: "Raw", desc: "Lightweight, aromatic, medium-grain rice from South India." },
  { name: "Broken Rice", type: "Broken", desc: "Cost-effective broken rice for industrial and food processing." },
  { name: "100% Broken", type: "Broken", desc: "Fine broken rice for flour, starch, and brewing industries." },
  { name: "Swarna Parboiled", type: "Parboiled", desc: "Short grain parboiled rice, staple in many regions." },
];

const getTypeIcon = (type) => {
  switch (type) {
    case "Sella": return <Sun size={16} className="text-brand-gold" />;
    case "Steam": return <Flame size={16} className="text-brand-accent" />;
    case "Raw": return <Droplets size={16} className="text-brand-green" />;
    default: return <Wheat size={16} className="text-brand-muted" />;
  }
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function ProductsPage() {
  return (
    <div data-testid="products-page" className="pt-20">
      {/* Hero */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold mb-4">
              Our Products
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-brand-green leading-[0.95] mb-6">
              Rice Collection
            </h1>
            <p className="text-brand-muted text-base sm:text-lg font-sans max-w-2xl leading-relaxed">
              Explore our comprehensive range of premium Basmati and Non-Basmati
              rice varieties, each cultivated and processed to the highest
              international standards.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Tabs */}
      <section data-testid="products-tabs" className="pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Tabs defaultValue="basmati" className="w-full">
            <TabsList
              data-testid="product-tabs-list"
              className="bg-brand-sand border border-brand-gold/10 rounded-full p-1 mb-12 w-fit"
            >
              <TabsTrigger
                value="basmati"
                data-testid="tab-basmati"
                className="rounded-full px-8 py-3 text-sm font-sans font-medium data-[state=active]:bg-brand-green data-[state=active]:text-brand-bone"
              >
                Basmati Rice
              </TabsTrigger>
              <TabsTrigger
                value="non-basmati"
                data-testid="tab-non-basmati"
                className="rounded-full px-8 py-3 text-sm font-sans font-medium data-[state=active]:bg-brand-green data-[state=active]:text-brand-bone"
              >
                Non-Basmati Rice
              </TabsTrigger>
              <TabsTrigger
                value="pesticide-free"
                data-testid="tab-pesticide-free"
                className="rounded-full px-8 py-3 text-sm font-sans font-medium data-[state=active]:bg-brand-green data-[state=active]:text-brand-bone"
              >
                Pesticide Residue Free
              </TabsTrigger>
            </TabsList>

            <TabsContent value="basmati" data-testid="basmati-content">
              {/* Family Groups */}
              {["1121", "1718", "1401", "1509", "Pusa"].map((family, fi) => (
                <motion.div
                  key={family}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: fi * 0.1 }}
                  className="mb-12"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <Wheat size={20} className="text-brand-gold" />
                    <h3 className="font-serif text-2xl sm:text-3xl font-medium text-brand-green">
                      {family} Basmati
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {basmatiRice
                      .filter((r) => r.family === family)
                      .map((rice, i) => (
                        <motion.div
                          key={rice.name}
                          variants={fadeUp}
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true }}
                          custom={i}
                          className="group bg-white p-6 rounded-2xl shadow-[0_4px_20px_rgba(26,54,38,0.04)] border border-brand-gold/10 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(26,54,38,0.08)] transition-all duration-400"
                        >
                          <div className="flex items-center gap-2 mb-3">
                            {getTypeIcon(rice.type)}
                            <span className="text-xs uppercase tracking-wider text-brand-muted font-sans font-medium">
                              {rice.type}
                            </span>
                          </div>
                          <h4 className="font-serif text-xl font-medium text-brand-green mb-2">
                            {rice.name}
                          </h4>
                          <p className="text-brand-muted text-sm font-sans leading-relaxed">
                            {rice.desc}
                          </p>
                        </motion.div>
                      ))}
                  </div>
                </motion.div>
              ))}
            </TabsContent>

            <TabsContent value="non-basmati" data-testid="non-basmati-content">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {nonBasmatiRice.map((rice, i) => (
                  <motion.div
                    key={rice.name}
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={i}
                    className="group bg-white p-6 rounded-2xl shadow-[0_4px_20px_rgba(26,54,38,0.04)] border border-brand-gold/10 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(26,54,38,0.08)] transition-all duration-400"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      {getTypeIcon(rice.type)}
                      <span className="text-xs uppercase tracking-wider text-brand-muted font-sans font-medium">
                        {rice.type}
                      </span>
                    </div>
                    <h4 className="font-serif text-xl font-medium text-brand-green mb-2">
                      {rice.name}
                    </h4>
                    <p className="text-brand-muted text-sm font-sans leading-relaxed">
                      {rice.desc}
                    </p>
                  </motion.div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="pesticide-free" data-testid="pesticide-free-content">
              <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-10 flex items-start gap-3">
                <ShieldCheck size={22} className="text-green-700 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-serif text-lg font-medium text-green-800 mb-1">Pesticide Residue Free Certified</h4>
                  <p className="text-green-700 text-sm font-sans leading-relaxed">
                    All varieties in this category are tested and certified pesticide residue free, meeting the strictest international food safety standards for health-conscious markets.
                  </p>
                </div>
              </div>
              {["1121", "1718", "1509", "Pusa", "Sugandha", "Sharbati"].map((family, fi) => {
                const items = pesticideResidueRice.filter((r) => r.family === family);
                if (items.length === 0) return null;
                return (
                  <motion.div
                    key={family}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: fi * 0.1 }}
                    className="mb-12"
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <ShieldCheck size={20} className="text-brand-gold" />
                      <h3 className="font-serif text-2xl sm:text-3xl font-medium text-brand-green">
                        {family} PRF
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {items.map((rice, i) => (
                        <motion.div
                          key={rice.name}
                          variants={fadeUp}
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true }}
                          custom={i}
                          className="group bg-white p-6 rounded-2xl shadow-[0_4px_20px_rgba(26,54,38,0.04)] border border-green-200/50 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(26,54,38,0.08)] transition-all duration-400"
                        >
                          <div className="flex items-center gap-2 mb-3">
                            {getTypeIcon(rice.type)}
                            <span className="text-xs uppercase tracking-wider text-brand-muted font-sans font-medium">
                              {rice.type}
                            </span>
                            <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-sans font-semibold rounded-full uppercase tracking-wider">
                              PRF
                            </span>
                          </div>
                          <h4 className="font-serif text-xl font-medium text-brand-green mb-2">
                            {rice.name}
                          </h4>
                          <p className="text-brand-muted text-sm font-sans leading-relaxed">
                            {rice.desc}
                          </p>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Packaging Showcase */}
      <section data-testid="packaging-section" className="py-24 lg:py-32 bg-brand-sand">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold mb-4">
              Our Packaging
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-brand-green">
              Brand Packaging Design
            </h2>
            <p className="text-brand-muted text-base font-sans max-w-2xl mx-auto mt-4 leading-relaxed">
              Every bag of Fab Crops rice is packaged with care, reflecting the premium quality inside. Custom packaging available for private label clients.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="bg-white rounded-3xl overflow-hidden shadow-[0_8px_32px_rgba(26,54,38,0.06)] border border-brand-gold/10 p-6"
            >
              <img
                src="/images/1121_packaging.png"
                alt="Fab Crops 1121 Basmati Rice Packaging"
                className="w-full h-auto rounded-2xl object-contain"
              />
              <p className="text-center font-serif text-xl font-medium text-brand-green mt-4">
                1121 Basmati Rice
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="bg-white rounded-3xl overflow-hidden shadow-[0_8px_32px_rgba(26,54,38,0.06)] border border-brand-gold/10 p-6"
            >
              <img
                src="/images/golden_sella_packaging.png"
                alt="Fab Crops Golden Sella Rice Packaging"
                className="w-full h-auto rounded-2xl object-contain"
              />
              <p className="text-center font-serif text-xl font-medium text-brand-green mt-4">
                Golden Sella Rice
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quality Promise */}
      <section data-testid="quality-section" className="py-24 lg:py-32 bg-brand-green">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold mb-4">
              Our Commitment
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-brand-bone mb-6">
              Quality in Every Grain
            </h2>
            <p className="text-brand-bone/70 text-base font-sans max-w-2xl mx-auto leading-relaxed mb-12">
              From farm to port, every batch of Fab Crops rice undergoes multiple
              stages of quality inspection including sortex cleaning, moisture
              testing, and grain analysis.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { num: "100%", label: "Sortex Clean" },
                { num: "12%", label: "Max Moisture" },
                { num: "0%", label: "Adulterants" },
                { num: "A+", label: "Grade Quality" },
              ].map((s) => (
                <div key={s.label}>
                  <p className="font-serif text-3xl lg:text-4xl font-light text-brand-gold">
                    {s.num}
                  </p>
                  <p className="text-brand-bone/60 text-xs font-sans uppercase tracking-wider mt-2">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
